import { Node, mergeAttributes } from '@tiptap/core';

// The gating divider: everything below it is for the post's audience. Two modes:
//
// - Plain (the default): the divider carries its own plan picker (the TargetingComponent in
//   dashboard/shared/tiptap/_gate_editor.html.erb) and stores the plans in data-gate-plan-ids.
// - Audience (capabilities.audience, set when the post has the audience picker, see
//   Block::PostAudienceComponent): the audience is picked beside the newsletter's name, and the editor
//   adds, syncs and removes the divider as it changes (applyAudience). The divider only holds where it
//   sits, plus the audience it was synced from: data-gate-plan-ids, or data-gate-paid-only for all paid
//   plans. Post#sync_gate_conditions reads both on save.

export const Gate = Node.create({
  name: 'gate',
  group: 'block',
  atom: true,

  addOptions() {
    return {
      templates: null, // { editor_html, audience_editor_html } — injected by tiptap controller from server template
      capabilities: null, // { audience: true, who: 'Premium' } when the post has the audience picker
    };
  },

  addAttributes() {
    return {
      gatePlanIds: {
        default: '',
        parseHTML: el => el.getAttribute('data-gate-plan-ids') || '',
        renderHTML: attrs => ({ 'data-gate-plan-ids': attrs.gatePlanIds }),
      },
      // All paid plans, so a plan added later is covered. Only written once the audience picker is in use.
      gatePaidOnly: {
        default: false,
        parseHTML: el => el.getAttribute('data-gate-paid-only') === 'true',
        renderHTML: attrs => (attrs.gatePaidOnly ? { 'data-gate-paid-only': 'true' } : {}),
      },
      // Editor-only state, never saved.
      gateLabel: { default: '', rendered: false },
      gateTip: { default: false, rendered: false },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-gate]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-gate': '' }, HTMLAttributes)];
  },

  addNodeView() {
    return (props) => {
      if (this.options.capabilities?.audience) return audienceNodeView(this, props);
      return plainNodeView(this, props);
    };
  },

  addCommands() {
    return {
      insertGate: (attrs = {}) => ({ chain }) =>
        chain().insertContent({ type: this.name, attrs }).run(),
    };
  },
});

function plainNodeView(extension, { node, getPos, editor }) {
  const dom = document.createElement('div');
  const editorHtml = extension.options.templates?.editor_html;
  dom.innerHTML = editorHtml || '<div class="border-2 border-dashed border-indigo-300 rounded p-3 text-sm text-indigo-600">GATE BELOW</div>';

  // Remove gate button
  dom.querySelector('[data-remove]')?.addEventListener('mousedown', e => {
    e.preventDefault();
    const pos = typeof getPos === 'function' ? getPos() : null;
    if (pos !== null) {
      const current = editor.state.doc.nodeAt(pos);
      if (current) {
        editor.chain().focus().deleteRange({ from: pos, to: pos + current.nodeSize }).run();
      }
    }
  });

  // When TargetingComponent changes, sync plan IDs back to Tiptap attrs. Gates target plans
  // only (no billing interval), so a data-gate-price-interval on an older gate isn't parsed
  // and drops off on the next save. TargetingComponent's Stimulus controller fires change
  // events and stores values in planSelectTarget (via TomSelect).
  dom.addEventListener('change', () => {
    const planSelectEl = dom.querySelector('[data-targeting-component-target="planSelect"]');

    const planIds = planSelectEl?.tomselect
      ? planSelectEl.tomselect.getValue()  // TomSelect returns comma-separated string for multi
      : '';
    const pos = typeof getPos === 'function' ? getPos() : null;
    if (pos !== null) {
      editor.chain().setNodeSelection(pos)
        .updateAttributes('gate', { gatePlanIds: planIds })
        .run();
    }
  });

  // After TargetingComponent's Stimulus controller initialises TomSelect, set initial
  // values from the node's existing attrs (needed when loading a post that already has a gate).
  if (node.attrs.gatePlanIds) {
    const trySetInitialValues = () => {
      const planSelectEl = dom.querySelector('[data-targeting-component-target="planSelect"]');
      if (!planSelectEl?.tomselect) return false;

      const ids = node.attrs.gatePlanIds.split(',').filter(Boolean);
      if (ids.length) {
        // Switch to "targeted" radio so TargetingComponent shows the selects
        const targetedRadio = dom.querySelector('input[type="radio"][value="targeted"]');
        if (targetedRadio && !targetedRadio.checked) {
          targetedRadio.click();
        }
        planSelectEl.tomselect.setValue(ids, true /* silent */);
      }
      return true;
    };

    // TomSelect init is async (fetchPlans is awaited). Poll briefly via MutationObserver.
    const observer = new MutationObserver(() => {
      if (trySetInitialValues()) observer.disconnect();
    });
    observer.observe(dom, { childList: true, subtree: true });
    // Fallback disconnect after 5 s to avoid leaks
    setTimeout(() => observer.disconnect(), 5000);
  }

  return {
    dom,
    update(updatedNode) {
      // Must return true to prevent Tiptap from destroying and recreating the
      // nodeview on every attr change (which would reset TomSelect's selection).
      if (updatedNode.type.name !== 'gate') return false;
      return true;
    },
  };
}

// ---- Audience mode ----------------------------------------------------------------------------------------

// Who the divider names, from the audience picker's choice (mirrors Block::PostAudienceComponent.who_label).
export function whoLabel(mode, names = []) {
  if (mode === 'everyone') return 'Everyone';
  if (mode === 'paid') return 'Paid subscribers';
  if (mode === 'subscribers') return 'Subscribers';
  if (names.length > 2) return `${names.length} plans`;
  return names.join(' & ') || 'Selected plans';
}

export function findGate(doc) {
  let found = null;
  doc.descendants((node, pos) => {
    if (found) return false;
    if (node.type.name === 'gate') found = { node, pos };
    return !found;
  });
  return found;
}

// Where the divider sits among the post's top-level blocks: its index, and how many blocks it can sit before.
export function gatePlacement(doc, gate = findGate(doc)) {
  if (!gate) return null;
  const topLevel = doc.resolve(gate.pos).depth === 0;
  return { index: doc.resolve(gate.pos).index(0), count: doc.childCount - (topLevel ? 1 : 0) };
}

function hasText(node) {
  const text = node.textContent.trim();
  return text !== '' && !text.startsWith('/');
}

// The first paragraph with text, skipping empty paragraphs, headings and images. Where the divider goes
// after it, or null when nothing follows that paragraph yet. `started` says whether any paragraph has text.
export function autoInsertPoint(doc) {
  const blocks = [];
  doc.forEach((node, offset) => blocks.push({ node, offset }));
  const at = blocks.findIndex(({ node }) => node.type.name === 'paragraph' && hasText(node));
  if (at < 0) return { pos: null, started: false };
  if (at === blocks.length - 1) return { pos: null, started: true };
  return { pos: blocks[at].offset + blocks[at].node.nodeSize, started: true };
}

function audienceAttrs({ mode, planIds = [], who }) {
  return {
    gatePlanIds: mode === 'plans' ? planIds.join(',') : '',
    gatePaidOnly: mode === 'paid',
    gateLabel: who,
  };
}

// Brings the divider in line with the audience, in transactions so the editor's autosave carries it.
// Returns what happened: { inserted }, { removed } or { pending } (the divider waits for a following block).
export function applyAudience(editor, audience) {
  const { state, view } = editor;
  const gate = findGate(state.doc);

  if (audience.mode === 'everyone') {
    if (!gate) return {};
    view.dispatch(state.tr.delete(gate.pos, gate.pos + gate.node.nodeSize));
    return { removed: true };
  }

  const attrs = audienceAttrs(audience);
  if (gate) {
    view.dispatch(state.tr.setNodeMarkup(gate.pos, undefined, { ...gate.node.attrs, ...attrs }));
    return {};
  }

  const { pos } = autoInsertPoint(state.doc);
  if (pos === null) return { pending: true };

  view.dispatch(state.tr.insert(pos, state.schema.nodes.gate.create({ ...attrs, gateTip: true })));
  return { inserted: true };
}

// Moves the divider so it sits before the k-th of the other top-level blocks (0 is above everything).
export function moveGateTo(editor, k) {
  const { state, view } = editor;
  const gate = findGate(state.doc);
  if (!gate) return;

  const tr = state.tr.delete(gate.pos, gate.pos + gate.node.nodeSize);
  const target = Math.max(0, Math.min(k, tr.doc.childCount - 1));
  let pos = 0;
  for (let i = 0; i < target; i++) pos += tr.doc.child(i).nodeSize;

  tr.insert(pos, gate.node.type.create({ ...gate.node.attrs, gateTip: false }));
  view.dispatch(tr);
}

function setTip(editor, getPos, show) {
  const pos = getPos();
  const node = typeof pos === 'number' ? editor.state.doc.nodeAt(pos) : null;
  if (!node || node.attrs.gateTip === show) return;
  editor.view.dispatch(editor.state.tr
    .setNodeMarkup(pos, undefined, { ...node.attrs, gateTip: show })
    .setMeta('addToHistory', false));
}

function audienceNodeView(extension, { node, getPos, editor }) {
  const dom = document.createElement('div');
  dom.innerHTML = extension.options.templates?.audience_editor_html
    || '<div class="my-4 border-2 border-dashed border-indigo-300 rounded p-3 text-sm text-indigo-600" data-gate-editor><span data-gate-pill></span><p data-gate-hint></p></div>';

  const pill = dom.querySelector('[data-gate-pill]');
  const hint = dom.querySelector('[data-gate-hint]');
  const tip = dom.querySelector('[data-gate-tip]');
  const up = dom.querySelector('[data-gate-move="up"]');
  const down = dom.querySelector('[data-gate-move="down"]');
  let current = node;
  // getPos is undefined while the node is being created or once it has been removed.
  const placement = () => {
    const pos = getPos();
    return typeof pos === 'number' ? gatePlacement(editor.state.doc, { node: current, pos }) : null;
  };

  const render = () => {
    const where = placement();
    const who = current.attrs.gateLabel || extension.options.capabilities.who || 'Selected plans';
    if (pill) pill.textContent = `${who} only below`;
    // Only said where it isn't obvious: a divider above everything leaves the title as all anyone else sees.
    if (hint) hint.textContent = where?.index === 0 ? 'Everyone else only sees the title.' : '';
    if (up && where) up.disabled = where.index <= 0;
    if (down && where) down.disabled = where.index >= where.count - 1;
    if (tip) {
      tip.classList.toggle('hidden', !current.attrs.gateTip);
      tip.classList.toggle('flex', !!current.attrs.gateTip);
    }
  };

  const move = (delta) => {
    const where = placement();
    if (where) moveGateTo(editor, where.index + delta);
  };

  up?.addEventListener('click', () => move(-1));
  down?.addEventListener('click', () => move(1));
  dom.querySelector('[data-gate-dismiss-tip]')?.addEventListener('click', () => setTip(editor, getPos, false));

  // Dragging: the handle and the pill start it; a line with a label shows where the divider will land.
  let drag = null;
  const wrapper = () => editor.view.dom.parentElement;

  const blocks = () => {
    const list = [];
    editor.state.doc.forEach((child, offset) => {
      if (child.type.name !== 'gate') list.push(editor.view.nodeDOM(offset));
    });
    return list.filter(Boolean);
  };

  const targetFor = (clientY) => {
    const list = blocks();
    for (let i = 0; i < list.length; i++) {
      const rect = list[i].getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) return i;
    }
    return Math.max(list.length - 1, 0);
  };

  const showIndicator = (k) => {
    const list = blocks();
    const where = placement();
    if (!list[k] || k === where?.index) {
      drag.indicator.classList.add('hidden');
      return;
    }
    const top = list[k].getBoundingClientRect().top - wrapper().getBoundingClientRect().top - 8;
    drag.indicator.style.top = `${top}px`;
    drag.indicator.classList.remove('hidden');
  };

  const onDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    drag.k = targetFor(event.clientY);
    showIndicator(drag.k);
  };

  const endDrag = () => {
    if (!drag) return;
    wrapper().removeEventListener('dragover', onDragOver, true);
    wrapper().removeEventListener('drop', onDrop, true);
    drag.indicator.remove();
    dom.classList.remove('opacity-40');
    drag = null;
  };

  function onDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    const k = drag?.k;
    const where = placement();
    endDrag();
    if (k !== null && k !== undefined && where && k !== where.index) moveGateTo(editor, k);
  }

  const startDrag = (event) => {
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', ' ');
      event.dataTransfer.setDragImage(dom, 16, 16);
    }
    const indicator = document.createElement('div');
    indicator.className = 'hidden absolute -inset-x-2 z-10 border-0 border-t-2 border-solid border-indigo-500 pointer-events-none';
    indicator.setAttribute('aria-hidden', 'true');
    indicator.innerHTML = '<span class="absolute left-1/2 -top-3 -translate-x-1/2 whitespace-nowrap rounded-full bg-indigo-500 px-2 py-0.5 text-xs font-semibold text-white">Move divider here</span>';
    wrapper().classList.add('relative');
    wrapper().appendChild(indicator);
    drag = { indicator, k: null };
    // Capture on the editor's wrapper, ahead of ProseMirror, which would paste the dragged text.
    wrapper().addEventListener('dragover', onDragOver, true);
    wrapper().addEventListener('drop', onDrop, true);
    // The browser takes its drag image from the element as it is when dragging starts.
    setTimeout(() => dom.classList.add('opacity-40'), 0);
    setTip(editor, getPos, false);
  };

  dom.querySelectorAll('[data-gate-drag]').forEach((handle) => {
    handle.addEventListener('dragstart', startDrag);
    handle.addEventListener('dragend', endDrag);
  });

  // Siblings change without this node changing, which moves its index and so its hint and arrows.
  editor.on('transaction', render);
  render();

  return {
    dom,
    update(updatedNode) {
      if (updatedNode.type.name !== 'gate') return false;
      current = updatedNode;
      render();
      return true;
    },
    // Its buttons and handles are its own: ProseMirror must not select the node or start its own drag.
    stopEvent: () => true,
    ignoreMutation: () => true,
    destroy() {
      editor.off('transaction', render);
      endDrag();
    },
  };
}

export const gateSlashItem = {
  title: 'Gating divider',
  description: 'Lock everything below behind a subscription',
  icon: '🔒',
  group: 'Blocks',
  keywords: ['gate', 'lock', 'paywall', 'members', 'subscribers', 'paid'],
  canInsert: (editor) => {
    // With the audience picker the divider comes with the audience and isn't added by hand.
    const gate = editor.extensionManager.extensions.find(extension => extension.name === 'gate');
    if (gate?.options.capabilities?.audience) return false;

    let hasGate = false;
    editor.state.doc.descendants(node => { if (node.type.name === 'gate') hasGate = true; });
    return !hasGate;
  },
  command: ({ editor, range }) => {
    editor.chain().focus().deleteRange(range).insertGate().run();
  },
};
